import { formatInTimeZone } from 'date-fns-tz';
import { parseISO, isValid } from 'date-fns';

const FALLBACK_TIMEZONE = 'UTC';

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

/**
 * Curated IANA zone → human abbreviation map.
 *
 * JS `Intl.DateTimeFormat` only ships real abbreviations for a small set of
 * zones (US zones + UTC); everything else (e.g. Asia/Kolkata) falls back to a
 * "GMT+5:30" offset string. To render proper abbreviations like "IST" we keep
 * our own map. A `{ std, dst }` entry is daylight-saving aware — we pick the
 * right code from the date's UTC offset. Zones NOT in this map fall back to the
 * `GMT+X` offset form (same as today), so nothing ever renders blank.
 *
 * Note: some abbreviations genuinely collide across regions (CST = China and
 * US-Central, AST = Arabia and Atlantic, IST = India and Ireland). We always
 * pair the abbreviation with the IANA zone where it matters (settings card), so
 * the abbreviation alone never has to be unambiguous.
 */
const TZ_ABBREVIATIONS: Record<string, string | { std: string; dst: string }> = {
  UTC: 'UTC',

  // ── South Asia (no DST) ──
  'Asia/Kolkata': 'IST', // +5:30  India
  'Asia/Colombo': 'IST', // +5:30  Sri Lanka
  'Asia/Karachi': 'PKT', // +5
  'Asia/Dhaka': 'BST', // +6  Bangladesh Standard Time
  'Asia/Kathmandu': 'NPT', // +5:45
  'Asia/Yangon': 'MMT', // +6:30

  // Common IANA aliases — same zone, different spellings tenants sometimes
  // store. Keep these in sync with their canonical entries above.
  'Asia/Calcutta': 'IST', // alias of Asia/Kolkata
  'Asia/Saigon': 'ICT',   // alias of Asia/Ho_Chi_Minh
  'Asia/Rangoon': 'MMT',  // alias of Asia/Yangon

  // ── Gulf / Middle East (no DST) ──
  'Asia/Dubai': 'GST', // +4
  'Asia/Riyadh': 'AST', // +3
  'Asia/Tehran': 'IRST', // +3:30 (Iran dropped DST in 2022)

  // ── East / SE Asia (no DST) ──
  'Asia/Singapore': 'SGT', // +8
  'Asia/Kuala_Lumpur': 'MYT', // +8
  'Asia/Hong_Kong': 'HKT', // +8
  'Asia/Shanghai': 'CST', // +8  China Standard Time
  'Asia/Tokyo': 'JST', // +9
  'Asia/Seoul': 'KST', // +9
  'Asia/Bangkok': 'ICT', // +7
  'Asia/Ho_Chi_Minh': 'ICT', // +7
  'Asia/Jakarta': 'WIB', // +7
  'Asia/Manila': 'PST', // +8  Philippine Standard Time — matches glibc %Z
                       //       (collides with US Pacific; pair with the IANA
                       //       zone where ambiguity matters)

  'Europe/London': { std: 'GMT', dst: 'BST' },
  'Europe/Paris': { std: 'CET', dst: 'CEST' },
  'Europe/Berlin': { std: 'CET', dst: 'CEST' },
  'Europe/Madrid': { std: 'CET', dst: 'CEST' },
  'Europe/Rome': { std: 'CET', dst: 'CEST' },
  'Europe/Amsterdam': { std: 'CET', dst: 'CEST' },
  'Europe/Athens': { std: 'EET', dst: 'EEST' },
  'Europe/Helsinki': { std: 'EET', dst: 'EEST' },

  // ── Africa (mostly no DST) ──
  'Africa/Nairobi': 'EAT', // +3  East Africa Time
  'Africa/Dar_es_Salaam': 'EAT',
  'Africa/Addis_Ababa': 'EAT',
  'Africa/Lagos': 'WAT', // +1  West Africa Time
  'Africa/Kinshasa': 'WAT',
  'Africa/Harare': 'CAT', // +2  Central Africa Time
  'Africa/Maputo': 'CAT',
  'Africa/Johannesburg': 'SAST', // +2  South Africa
  'Africa/Accra': 'GMT', // +0  Ghana
  'Africa/Cairo': { std: 'EET', dst: 'EEST' }, // Egypt reinstated DST in 2023

  'America/New_York': { std: 'EST', dst: 'EDT' },
  'America/Toronto': { std: 'EST', dst: 'EDT' },
  'America/Chicago': { std: 'CST', dst: 'CDT' },
  'America/Denver': { std: 'MST', dst: 'MDT' },
  'America/Phoenix': 'MST', // Arizona: no DST
  'America/Los_Angeles': { std: 'PST', dst: 'PDT' },
  'America/Anchorage': { std: 'AKST', dst: 'AKDT' },

  // ── Latin America (no DST since 2019/2022) ──
  'America/Sao_Paulo': 'BRT', // -3  Brazil
  'America/Argentina/Buenos_Aires': 'ART', // -3
  'America/Mexico_City': 'CST', // -6

  // ── Australia / NZ (DST in southern summer) ──
  'Australia/Sydney': { std: 'AEST', dst: 'AEDT' },
  'Australia/Adelaide': { std: 'ACST', dst: 'ACDT' },
  'Australia/Perth': 'AWST', // no DST
  'Pacific/Auckland': { std: 'NZST', dst: 'NZDT' },
};

// `Intl.DateTimeFormat` construction is the expensive part, so reuse one
// formatter per zone (these helpers run on the render path, e.g. per table row).
const _offsetFormatters = new Map<string, Intl.DateTimeFormat | null>();
function offsetFormatter(zone: string): Intl.DateTimeFormat | null {
  if (_offsetFormatters.has(zone)) return _offsetFormatters.get(zone)!;
  let f: Intl.DateTimeFormat | null = null;
  try {
    f = new Intl.DateTimeFormat('en', { timeZone: zone, timeZoneName: 'shortOffset' });
  } catch {
    f = null;
  }
  _offsetFormatters.set(zone, f);
  return f;
}

/**
 * The `GMT±H[:MM]` offset label for a zone at a given instant (DST-aware).
 * Used as the fallback when a zone is not in `TZ_ABBREVIATIONS`. Returns "UTC"
 * for the zero offset and "UTC" if the zone is invalid.
 */
function offsetLabel(zone: string, date: Date): string {
  const f = offsetFormatter(zone);
  if (!f) return 'UTC';
  const v = f.formatToParts(date).find((p) => p.type === 'timeZoneName')?.value;
  if (!v || v === 'GMT' || v === 'UTC') return 'UTC';
  return v; // e.g. "GMT+5:30"
}

/** Minutes east of UTC for `zone` at `date` (DST-aware). 0 if unknown. */
function offsetMinutes(zone: string, date: Date): number {
  const label = offsetLabel(zone, date);
  const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(label);
  if (!m) return 0; // "UTC"
  const sign = m[1] === '+' ? 1 : -1;
  return sign * (parseInt(m[2], 10) * 60 + (m[3] ? parseInt(m[3], 10) : 0));
}

// Standard-time offset per `${zone}:${year}` (null = zone has no DST). The two
// probe lookups only happen once per zone/year, not on every format call.
const _stdOffsetByZoneYear = new Map<string, number | null>();
function standardOffsetMinutes(zone: string, year: number): number | null {
  const key = `${zone}:${year}`;
  const cached = _stdOffsetByZoneYear.get(key);
  if (cached !== undefined) return cached;
  const jan = offsetMinutes(zone, new Date(Date.UTC(year, 0, 1)));
  const jul = offsetMinutes(zone, new Date(Date.UTC(year, 6, 1)));
  const std = jan === jul ? null : Math.min(jan, jul);
  _stdOffsetByZoneYear.set(key, std);
  return std;
}

/**
 * True if `date` falls in daylight-saving time for `zone`. Works in both
 * hemispheres: standard time always has the smaller UTC offset of the year, so
 * any offset greater than that minimum means DST is active. Memoized per
 * zone/year so it's cheap to call on the render path.
 */
function isDST(zone: string, date: Date): boolean {
  const std = standardOffsetMinutes(zone, date.getUTCFullYear());
  if (std === null) return false; // zone does not observe DST
  return offsetMinutes(zone, date) > std;
}

/**
 * Human-friendly abbreviation for an IANA zone at a given instant, e.g.
 * "IST", "EST"/"EDT", "AEST"/"AEDT". Falls back to the `GMT+X` offset form for
 * zones not in the curated map. Never throws.
 *
 * `preferStandard: true` always returns the standard-time abbreviation even
 * mid-summer — for static labels (e.g. a settings card) that shouldn't flip
 * between EST/EDT with the season.
 */
export function tzAbbreviation(
  zone: string,
  date: Date = new Date(),
  opts: { preferStandard?: boolean } = {},
): string {
  if (!zone) return 'UTC';
  const entry = TZ_ABBREVIATIONS[zone];
  if (typeof entry === 'string') return entry;
  if (entry) {
    if (opts.preferStandard) return entry.std;
    return isDST(zone, date) ? entry.dst : entry.std;
  }
  return offsetLabel(zone, date);
}

/**
 * Strip standalone date-fns zone tokens (`z`/`zz`/`zzz`/`zzzz`) from a format
 * string, leaving `z` inside single-quoted literals intact (date-fns uses
 * `'…literal…'` for escaping). Returns the trimmed format and whether any
 * zone token was found in unquoted text.
 */
function stripZoneTokens(fmt: string): { stripped: string; hasZone: boolean } {
  let hasZone = false;
  // Alternating split keeps quoted segments at odd indices and unquoted at even.
  const parts = fmt.split(/('(?:[^']|'')*')/g);
  const out = parts.map((part, i) => {
    if (i % 2 === 1) return part; // quoted literal — leave untouched
    if (/z/.test(part)) hasZone = true;
    return part.replace(/\s*z{1,4}/g, '');
  });
  return { stripped: out.join('').trim(), hasZone };
}

/**
 * Format an ISO datetime string. By default renders in the user's browser
 * timezone — pass an explicit IANA zone (e.g. the tenant's `display_timezone`)
 * to render the same wall-clock time for every viewer regardless of browser TZ.
 *
 * When the format string contains a zone token (`z`/`zzz`), the curated
 * abbreviation ("IST") is appended instead of the platform's "GMT+5:30".
 */
export function formatDateTime(
  isoString: string | null | undefined,
  timezone?: string,
  fmt: string = 'MMM d, yyyy h:mm a zzz',
): string {
  if (!isoString) return '';
  const date = parseISO(isoString);
  if (!isValid(date)) return '';
  const tz = timezone || browserTimezone();
  const { stripped: baseFmt, hasZone: hasZoneToken } = stripZoneTokens(fmt);
  // When the format is only a zone token (e.g. "z"), baseFmt is empty — return
  // just the abbreviation. (date-fns `format` throws on an empty format string,
  // and we'd otherwise emit a leading space.)
  if (!baseFmt) {
    try {
      return tzAbbreviation(tz, date);
    } catch {
      return tzAbbreviation(FALLBACK_TIMEZONE, date);
    }
  }
  try {
    const base = formatInTimeZone(date, tz, baseFmt);
    return hasZoneToken ? `${base} ${tzAbbreviation(tz, date)}` : base;
  } catch {
    const base = formatInTimeZone(date, FALLBACK_TIMEZONE, baseFmt);
    return hasZoneToken ? `${base} ${tzAbbreviation(FALLBACK_TIMEZONE, date)}` : base;
  }
}

export function formatDate(
  isoString: string | null | undefined,
  timezone?: string,
  fmt: string = 'MMM d, yyyy',
): string {
  return formatDateTime(isoString, timezone, fmt);
}

export function formatTime(
  isoString: string | null | undefined,
  timezone?: string,
  fmt: string = 'h:mm a zzz',
): string {
  return formatDateTime(isoString, timezone, fmt);
}

export { browserTimezone };
